// src/pages/CheckinStart.js
//
// Single-step flow: pick questions. (Mode is hardcoded to async for v1;
// sync mode is a Phase 2 feature.)
//
import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { theme } from '../lib/theme'

const c = theme.colors
const f = theme.fonts

const CATEGORIES = [
  { key: 'reflect', label: 'Reflect', emoji: '💭', desc: 'Look back on the month' },
  { key: 'share',   label: 'Share',   emoji: '💬', desc: 'Open up about feelings' },
  { key: 'imagine', label: 'Imagine', emoji: '✨', desc: 'Dream about the future' },
  { key: 'decide',  label: 'Decide',  emoji: '🎯', desc: 'Make plans together' },
]

export default function CheckinStart() {
  const { id: conversationId } = useParams()
  const navigate = useNavigate()
  const [conversation, setConversation] = useState(null)
  const [library, setLibrary] = useState([])
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [customText, setCustomText] = useState('')
  const [customQuestions, setCustomQuestions] = useState([])
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchAll() }, [conversationId])

  async function fetchAll() {
    const { data: convo } = await supabase.from('money_conversations')
      .select('*')
      .eq('id', conversationId)
      .maybeSingle()
    if (!convo) {
      navigate('/checkins')
      return
    }
    setConversation(convo)

    const { data: { user } } = await supabase.auth.getUser()
    const { data: questions } = await supabase.from('question_library')
      .select('*')
      .or(`user_id.is.null,user_id.eq.${user.id}`)
      .eq('is_active', true)
      .order('sort_order', { ascending: true })
    setLibrary(questions || [])
    setLoading(false)
  }

  const toggleSelect = (libId) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(libId)) next.delete(libId)
      else next.add(libId)
      return next
    })
  }

  const addCustom = () => {
    const text = customText.trim()
    if (!text) return
    setCustomQuestions(prev => [...prev, { id: `custom-${Date.now()}`, text }])
    setCustomText('')
  }

  const removeCustom = (id) => {
    setCustomQuestions(prev => prev.filter(q => q.id !== id))
  }

  const totalQuestions = selectedIds.size + customQuestions.length

  const finishSetup = async () => {
    if (totalQuestions < 1) return
    setSaving(true)

    const { data: { user } } = await supabase.auth.getUser()
    const inserts = []
    let order = 1

    for (const libId of selectedIds) {
      inserts.push({
        conversation_id: conversationId,
        question_library_id: libId,
        sort_order: order++,
      })
    }

    for (const cq of customQuestions) {
      const { data: newLib } = await supabase.from('question_library')
        .insert({
          user_id: user.id,
          text: cq.text,
          category: 'custom',
          type: 'text',
          sort_order: 999,
        })
        .select()
        .single()

      if (newLib) {
        inserts.push({
          conversation_id: conversationId,
          question_library_id: newLib.id,
          sort_order: order++,
        })
      }
    }

    await supabase.from('conversation_questions').insert(inserts)
    await supabase.from('money_conversations')
      .update({ status: 'awaiting_partner' })
      .eq('id', conversationId)

    setSaving(false)
    navigate(`/checkins/${conversationId}/answer`)
  }

  const cancelSetup = async () => {
    if (!window.confirm('Cancel this check-in? You can start a new one anytime.')) return
    await supabase.from('money_conversations').delete().eq('id', conversationId)
    navigate('/checkins')
  }

  if (loading) {
    return <div style={styles.loading}>Loading…</div>
  }

  const grouped = CATEGORIES.map(cat => ({
    ...cat,
    questions: library.filter(q => q.category === cat.key),
  }))
  const customCategoryQuestions = library.filter(q => q.category === 'custom')

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <button onClick={() => navigate('/checkins')} style={styles.backBtn}>
          ‹ Cancel
        </button>
        <h1 style={styles.title}>Pick your questions</h1>
        <p style={styles.tagline}>
          Choose 3 to 6 questions to answer with your partner. Add your own if you want.
        </p>
        <div style={styles.counter}>
          {totalQuestions === 0 ? (
            <span>None selected</span>
          ) : (
            <span>{totalQuestions} selected</span>
          )}
        </div>
      </header>

      {grouped.map(cat => cat.questions.length > 0 && (
        <section key={cat.key} style={styles.categorySection}>
          <div style={styles.categoryHeader}>
            <span style={styles.categoryEmoji}>{cat.emoji}</span>
            <div>
              <h2 style={styles.categoryTitle}>{cat.label}</h2>
              <p style={styles.categoryDesc}>{cat.desc}</p>
            </div>
          </div>
          <div style={styles.questionList}>
            {cat.questions.map(q => (
              <QuestionRow
                key={q.id}
                text={q.text}
                selected={selectedIds.has(q.id)}
                onClick={() => toggleSelect(q.id)}
              />
            ))}
          </div>
        </section>
      ))}

      {customCategoryQuestions.length > 0 && (
        <section style={styles.categorySection}>
          <div style={styles.categoryHeader}>
            <span style={styles.categoryEmoji}>📝</span>
            <div>
              <h2 style={styles.categoryTitle}>Your custom questions</h2>
              <p style={styles.categoryDesc}>Saved from previous check-ins</p>
            </div>
          </div>
          <div style={styles.questionList}>
            {customCategoryQuestions.map(q => (
              <QuestionRow
                key={q.id}
                text={q.text}
                selected={selectedIds.has(q.id)}
                onClick={() => toggleSelect(q.id)}
              />
            ))}
          </div>
        </section>
      )}

      <section style={styles.customSection}>
        <h2 style={styles.categoryTitle}>Add your own</h2>
        <p style={styles.categoryDesc}>Write a question that's specific to you. It'll be saved for next time too.</p>
        <div style={styles.customInputRow}>
          <input
            type="text"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addCustom() }}
            placeholder="What should we talk about?"
            style={styles.customInput}
          />
          <button onClick={addCustom} style={styles.addBtn} disabled={!customText.trim()}>
            Add
          </button>
        </div>

        {customQuestions.length > 0 && (
          <div style={styles.questionList}>
            {customQuestions.map(cq => (
              <div key={cq.id} style={styles.customQuestionRow}>
                <span style={styles.customQuestionText}>{cq.text}</span>
                <button onClick={() => removeCustom(cq.id)} style={styles.removeBtn} aria-label="Remove question">
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <div style={styles.actionRow}>
        <button onClick={cancelSetup} style={styles.cancelBtn}>
          Cancel check-in
        </button>
        <button
          onClick={finishSetup}
          style={{
            ...styles.primaryBtn,
            opacity: totalQuestions >= 1 && !saving ? 1 : 0.5,
            cursor: totalQuestions >= 1 && !saving ? 'pointer' : 'not-allowed',
          }}
          disabled={totalQuestions < 1 || saving}
        >
          {saving ? 'Saving…' : `Start answering (${totalQuestions})`}
        </button>
      </div>
    </div>
  )
}

function QuestionRow({ text, selected, onClick }) {
  return (
    <button onClick={onClick} style={{
      ...styles.questionRow,
      ...(selected ? styles.questionRowSelected : {}),
    }}>
      <div style={{
        ...styles.checkbox,
        ...(selected ? styles.checkboxSelected : {}),
      }}>
        {selected && <span aria-hidden="true">✓</span>}
      </div>
      <span style={styles.questionText}>{text}</span>
    </button>
  )
}

const styles = {
  page: { padding: '24px', maxWidth: '740px', margin: '0 auto' },
  loading: { padding: '40px', textAlign: 'center', color: c.inkMuted },
  header: { marginBottom: '24px' },
  backBtn: {
    background: 'none', border: 'none',
    color: c.inkMuted, fontSize: '13px',
    cursor: 'pointer', padding: '4px 0',
    display: 'inline-flex', alignItems: 'center', gap: '2px',
    marginBottom: '16px',
  },
  title: {
    fontFamily: f.display, fontSize: '28px', fontWeight: 600,
    color: c.plum, margin: 0, letterSpacing: '-0.02em',
  },
  tagline: {
    color: c.inkMuted, fontSize: '14px', margin: '6px 0 0',
    lineHeight: 1.5, maxWidth: '600px',
  },
  counter: {
    marginTop: '12px',
    fontSize: '13px', fontWeight: 600,
    color: c.green, fontFamily: f.display,
  },

  categorySection: {
    background: c.white, borderRadius: '14px', padding: '20px',
    border: `1px solid ${c.line}`, marginBottom: '14px',
  },
  categoryHeader: {
    display: 'flex', alignItems: 'flex-start', gap: '12px',
    marginBottom: '14px',
  },
  categoryEmoji: { fontSize: '24px', marginTop: '-2px' },
  categoryTitle: {
    fontFamily: f.display, fontSize: '17px', fontWeight: 600,
    color: c.plum, margin: 0,
  },
  categoryDesc: {
    fontSize: '13px', color: c.inkMuted, margin: '2px 0 0',
  },
  questionList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  questionRow: {
    background: c.cream2 || '#faf8f5',
    border: `1px solid ${c.line}`,
    borderRadius: '10px', padding: '12px 14px',
    display: 'flex', alignItems: 'center', gap: '12px',
    cursor: 'pointer', textAlign: 'left',
    fontFamily: 'inherit',
    transition: 'border 100ms, background 100ms',
  },
  questionRowSelected: {
    borderColor: c.green,
    background: c.greenSoft || '#e8f5e9',
  },
  checkbox: {
    width: '20px', height: '20px',
    borderRadius: '6px', border: `2px solid ${c.line}`,
    background: c.white,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
    color: c.white,
  },
  checkboxSelected: {
    background: c.green,
    borderColor: c.green,
  },
  questionText: {
    fontSize: '14px', color: c.ink, lineHeight: 1.4,
  },

  customSection: {
    background: c.white, borderRadius: '14px', padding: '20px',
    border: `1px solid ${c.line}`, marginBottom: '20px',
  },
  customInputRow: {
    display: 'flex', gap: '8px', marginTop: '12px',
  },
  customInput: {
    flex: 1,
    border: `1px solid ${c.line}`,
    borderRadius: '8px',
    padding: '10px 12px', fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
  },
  addBtn: {
    background: c.cream2 || '#faf8f5',
    border: `1px solid ${c.line}`,
    borderRadius: '8px',
    padding: '10px 14px', fontSize: '13px', fontWeight: 600,
    color: c.plum, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    fontFamily: 'inherit',
  },
  customQuestionRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: c.cream2, padding: '10px 14px',
    borderRadius: '8px', border: `1px solid ${c.line}`,
  },
  customQuestionText: { fontSize: '14px', color: c.ink },
  removeBtn: {
    background: 'none', border: 'none',
    color: c.inkMuted, cursor: 'pointer',
    padding: '4px',
    fontSize: '18px', lineHeight: 1,
  },

  primaryBtn: {
    background: c.green, color: c.white,
    border: 'none', borderRadius: '10px',
    padding: '14px 24px', fontSize: '15px', fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
    transition: 'background 100ms',
  },

  actionRow: {
    display: 'flex', justifyContent: 'space-between', gap: '12px',
    marginTop: '24px',
  },
  cancelBtn: {
    background: 'none', border: `1px solid ${c.line}`,
    color: c.inkMuted, borderRadius: '10px',
    padding: '14px 20px', fontSize: '14px', fontWeight: 500,
    cursor: 'pointer', fontFamily: 'inherit',
  },
}